import { useState } from "react";
import { COMPANY } from "@/lib/pricing";
import api from "@/lib/api";
import { toast } from "sonner";

export const EMAIL_RE = /^\S+@\S+\.\S+$/;

// All valid RFP emails for a carrier — every contact's email, falling back to the legacy single email.
export const carrierEmails = (c) =>
  (c.contacts?.length ? c.contacts.map((x) => x.email) : [c.email]).filter((e) => EMAIL_RE.test(e || ""));

// Owns recipient selection + the email send flow for the RFP modal.
export function useRfpEmail(rfpData, emailCarriers) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [extraEmails, setExtraEmails] = useState("");
  const [ccOwner, setCcOwner] = useState(true);
  const [ccAgent, setCcAgent] = useState(true);
  const [attachPdf, setAttachPdf] = useState(true);
  const [sending, setSending] = useState(false);

  const selectedCarriers = emailCarriers.filter((c) => selectedIds.includes(c.id));
  // Main sales agents are CC'd along with any per-customer agents.
  const mainAgents = rfpData.salesAgents || (rfpData.salesAgent?.email ? [rfpData.salesAgent] : []);
  const agentEmails = [
    ...new Set(
      [
        ...mainAgents.map((a) => (a.email || "").trim()),
        ...(rfpData.customers || []).map((c) => (c.agent?.email || "").trim()),
      ].filter((e) => EMAIL_RE.test(e))
    ),
  ];
  const typedEmails = extraEmails.split(",").map((s) => s.trim()).filter(Boolean);
  const recipients = [
    ...new Set([
      ...selectedCarriers.flatMap(carrierEmails),
      ...typedEmails.filter((e) => EMAIL_RE.test(e)),
      ...(ccAgent ? agentEmails : []),
    ]),
  ];

  const toggleCarrier = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const addSelections = (ids) => setSelectedIds((prev) => [...new Set([...prev, ...ids])]);

  const selectAllPreferred = () =>
    addSelections(emailCarriers.filter((c) => c.preferred).map((c) => c.id));

  const selectAll = () => setSelectedIds(emailCarriers.map((c) => c.id));

  const clearSelection = () => setSelectedIds([]);

  const sendEmail = async (html) => {
    const invalid = typedEmails.filter((e) => !EMAIL_RE.test(e));
    if (invalid.length) {
      toast.error(`Invalid email: ${invalid.join(", ")}`);
      return;
    }
    if (!recipients.length) {
      toast.error("Select at least one carrier or enter an email address");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/send-rfp", {
        recipient_emails: recipients,
        subject: `Request for Shipping Quote — ${COMPANY.name}`,
        html_content: html,
        reply_to: COMPANY.rfpEmail,
        cc_owner: ccOwner,
        attach_pdf: attachPdf,
        base_url: window.location.origin,
        carriers: selectedCarriers.map((c) => ({ id: c.id, name: c.name, email: c.email })),
        route_summary: `${rfpData.pickup || ""} → ${(rfpData.stops || []).filter((s) => s && s.trim()).join(" → ")}`,
      });
      toast.success(data.message || `RFP sent to ${recipients.length} recipient(s)`);
      setSelectedIds([]);
      setExtraEmails("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  return {
    selectedIds,
    selectedCarriers,
    extraEmails,
    setExtraEmails,
    ccOwner,
    setCcOwner,
    ccAgent,
    setCcAgent,
    attachPdf,
    setAttachPdf,
    sending,
    agentEmails,
    recipients,
    toggleCarrier,
    addSelections,
    selectAllPreferred,
    selectAll,
    clearSelection,
    sendEmail,
  };
}
